import useDesktopChecker from "hooks/useDesktopChecker";
import React, { useEffect } from "react";
import "./drawer.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import NewHeaderLogo from "assets/images/nafhub-light-logo.svg";
import { Button } from "../button/Button";
import { splitAndCapitalizeWords } from "utilities/string";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  handleDownload?: () => void;
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  closeText?: string;
  customCss?: string;
  footerText?: string;
  type?: DrawerType;
  hasError?: boolean;
  invalidReason?: string;
  invalidDescription?: string;
  subTitle?: string;
  centerContent?: boolean;
}

export enum DrawerType {
  ElectronicSignature,
  DocumentUpload,
  DocumentPreview,
  UqualAdvisorCard,
  Voa
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  customCss,
  closeText,
  footerText,
  type,
  handleDownload,
  hasError,
  invalidReason,
  invalidDescription,
  subTitle,
  centerContent = false,
}) => {
  const isDesktop = useDesktopChecker();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen]);

  function isNonEmptyString(value: string | null | undefined): boolean {
    return value != null && value.trim() !== "";
  }

  return (
    <>
      {isOpen && (
        <>
          <div className="drawer-overlay drawer-overlay--open" />
          <div
            className={`drawer drawer--open ${isDesktop ? "drawer--desktop" : "drawer--mobile"} ${customCss ?? ""} ${
              isDesktop ? (customCss ?? "") + "--desktop" : (customCss ?? "") + "--mobile"
            }`}
          >
            <div className={`drawer__header ${type == DrawerType.DocumentUpload || (!isDesktop && DrawerType.Voa) ? "drawer__header--no-border" : ""}`}>
              <div className="drawer__header-left">
                {type == DrawerType.ElectronicSignature && (
                  <>
                    <img className="drawer__logo" src={NewHeaderLogo} alt="nafhub logo" />
                    <FontAwesomeIcon className="drawer__chevron" icon={regular("chevron-right")} />
                  </>
                )}
                {type == DrawerType.Voa && isDesktop && (
                  <>
                    <img className="drawer__logo" src={NewHeaderLogo} alt="nafhub logo" />
                    <FontAwesomeIcon className="drawer__pipe" icon={regular("pipe")} />
                  </>
                )}
                <span className="drawer__title">{title}</span>
              </div>
              <FontAwesomeIcon onClick={onClose} role="button" aria-label="Close" className="drawer__close-btn" icon={regular("close")} />
            </div>
            {type == DrawerType.DocumentUpload && <div className="drawer__subtitle">{subTitle}</div>}
            {type == DrawerType.DocumentPreview && isNonEmptyString(invalidReason) && isNonEmptyString(invalidDescription) && (
              <div className="drawer__invalid-content">
                <div className="drawer__invalid-wrapper">
                  <FontAwesomeIcon className="drawer__caution-icon" icon={regular("triangle-exclamation")} />
                  <div className="drawer__invalid-reason">{splitAndCapitalizeWords(invalidReason ?? "")}</div>
                </div>
                <div className="drawer__invalid-description">{invalidDescription}</div>
              </div>
            )}
            {children && <div className={centerContent ? "drawer__body" : ""}>{children}</div>}

            {type == DrawerType.ElectronicSignature && (
              <div className="drawer__footer">
                <div className="drawer__footer--left">{footerText}</div>
                <div className="drawer__footer--right">
                  <Button title={closeText} type={"round"} variant={"primary"} onClick={onClose} />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Drawer;
